// useEnrolmentState.js
import { useState } from "react";
import { DEPOSIT_FREQUENCIES_FOR_QUOTE } from "../Utils/PlanUtils";
import { monthlyAvailableSavingsForRespContributions } from "../Utils/KYCUtils";
import packageJson from "../../package.json";

//Returns a collection of state variables and functions to update them, as needed by a specific enrolment.
//Much of what is defined here would map to what is stored in the backend
export default function useEnrolmentState() {
    //This custom state hook allows us to prevent updates if the enrolment is not in the pre-docusign state
    // In other words, once the enrolment has been submitted to DocuSign, we don't allow any more edits
    // The backend won't accept any changes anyway, but this makes it so the frontend inputs are not editable.
    const useCustomState = (initialState) => {
        const [state, setState] = useState(initialState);
        const setUseCustomState = (newState) => {
            if (enrollment.state !== "pre-docusign") {
                return;
            }
            setState(newState);
        };
        return [state, setUseCustomState];
    };

    const [user, setUser] = useCustomState({});

    const [aces, setAces] = useCustomState({
        address: "",
        city: "",
        grant8: false,
        grant11: false,
        grant14: false,
        grantBase: false,
        hundredDollarContrib: false,
        isAlbertaResidentSubscriber: false,
        legalGuardianName: "",
        postalCode: "",
        school8: "",
        school11: "",
        school14: "",
        schoolCity8: "",
        schoolCity11: "",
        schoolCity14: "",
        typeOfId: "",
    });
    const [agent, setAgent] = useCustomState({
        code: "",
        default: false,
        firstName: "",
        language: false,
        lastName: "",
        password: "",
        province: "",
        email: "",
        status: 0,
        inPersonSigner: true,
    });
    const [application, setApplication] = useCustomState({
        accountNumber: "",
        agreementNumber: "",
        anticipateContributions: false,
        applicationStarted: new Date(),
        bank: "",
        bankName: "",
        bcResidentTypeOfID: "",
        beneficiaryCertificateInscription: "",
        cardCVV: "",
        cardNumber: "",
        cardType: "",
        creditSubscriber: 1,
        custodialParentFirstName: "",
        custodialParentLastName: "",
        custodianAddress: "",
        custodianCity: "",
        custodianCountry: "Canada",
        custodianEmail: "",
        custodianFirstName: "",
        custodianGender: 0,
        custodianLastName: "",
        custodianMiddleName: "",
        custodianPobox: "",
        custodianPostalCode: "",
        custodianProvince: "",
        custodianSIN: "",
        custodianStreetNumber: "",
        custodianSuite: "",
        custodianTitle: "",
        custodianMobilePhone: "",
        custodianInPersonSigner: true,
        expiryMonth: "",
        expiryYear: "",
        grantCanadaEducationSavings: true,
        grantCanadaLearningBond: false,
        grantProvincial: true,
        //grantSAGES: false,
        includedForms: 0,
        papSubscriber: 1,
        signingDate: new Date(),
        specialInstructions: "",
        tcpEmail: "",
        tcpName: "",
        tcpPhone: "",
        tcpRelationship: "",
        transit: "",
        uploadSerialNumber: "",
        uploadStatus: 0,

        _enrollmentId: null,
    });
    const [beneficiary, setBeneficiary] = useCustomState({
        birthdate: "",
        firstName: "",
        gender: 0,
        lastName: "",
        middleName: "",
        relationshipToSubscriber: "",
        saskatchewanResidentSince: new Date(),
        sin: "",

        // these are only set when bene age is > 18
        email: "",
        mobilePhone: "",
        inPersonSigner: true,
    });
    const [common, setCommon] = useCustomState({
        agentCode: "",
        agentName: "",
        borrowingToInvest: false,
        preferedLanguage: true,
        prospectusDate: "",
        referralAgreement: false,
        specialReferralAgreement: false,
        representativeDetails: false,
        representativeDetailsLanguage: "",
        signingProvince: "",
        subscriber2Include: false,
        subscriberEngOrFr: true,
    });
    const [configuration, setConfiguration] = useCustomState({
        agentList: "",
        environment: "",
        lastCheckIn: new Date(),
        lockTimeMinutes: 0,
        selfDestruct: false,
        serverAddress: "",
        techSupportMode: false,
        upgradeDetected: false,
        upgradeMandatoryBy: new Date(),
        upgradeToVersion: "",
        upgradeURL: "",
        createdWithFrontendVersion: packageJson.version,
    });

    const [dependant, setDependant] = useCustomState({
        birthdate: "",
        name: "",
        yearsOfEducationalSupport: "4",
    });
    const [dependant2, setDependant2] = useCustomState({
        birthdate: "",
        name: "",
        yearsOfEducationalSupport: "4",
    });
    const [dependant3, setDependant3] = useCustomState({
        birthdate: "",
        name: "",
        yearsOfEducationalSupport: "4",
    });
    const [dependant4, setDependant4] = useCustomState({
        birthdate: "",
        name: "",
        yearsOfEducationalSupport: "4",
    });
    const [dependant5, setDependant5] = useCustomState({
        birthdate: "",
        name: "",
        yearsOfEducationalSupport: "4",
    });
    const [knowYourClient, setKnowYourClient] = useCustomState({
        afterMaternityLeaveEnds: "",
        ageOfYoungestBeneficiary: 0, // do not rely on this to be accurate, it is calculated thru calculateAgeOfYoungestBeneficiary
        ageOfYoungestBeneficiaryDefault: 0,
        aggressive: false,
        anticipateAbleToKeepContributions: false,
        anticipateKeepContributions: false,
        applyForCreditCard: false,
        // approximateNetWorth: '', calculated thru calculateApproxNetWorth
        approxNetWorth: 0,
        affordabilityGroup: false,
        affordabilityAchievers: false,
        affordabilityYesNo: false,
        afterMatParentsWill: "",
        availableSavingsForRespContributions: 0,
        balance: false,
        beneficiaryIsADependant: true,
        ccbMonthlyIncomeGross: 0.0,
        ccbMonthlyIncomeGrossAfterMat: 0.0,
        ccbNetAfterMat: 0.0,
        ccbGrossAfterMat: 0.0,
        childCare: 0,
        childCareAfterMat: 0,
        clothing: 0,
        clothingAfterMat: 0,
        deposits: 0,
        diExceeded: false,
        eightyYearRule: false,
        existingSubscriber: false,
        familyIncome: 0,
        familyIncomeAfterMat: 0,
        formIncomplete: false,
        groceries: 0,
        groceriesAfterMat: 0,
        growth: false,
        income: false,
        insurance: 0,
        insuranceAfterMat: 0,
        kycFormType: "",
        liabilities: 0,
        locationAndTypeOfSavings: "",
        maternityLeave: false,
        maximumEsp: 0,
        maximumEspGroup: 0,
        meetDealingRepresentative: 0,
        miscellaneous: 0,
        miscellaneousAfterMat: 0,
        // monthlyAvailableSavingsForRespContributions: 0, calculated thru monthlyAvailableSavingsForRespContributions
        morgageCondoFeesRent: 0,
        morgageCondoFeesRentAfterMat: 0,
        notes: "",
        numberOfDependants: 1,
        numberPurchasingScholarshipPlan: 1,
        objective: "",
        otherAssets: 0,
        otherContractualObligations: 0,
        otherContractualObligationsAfterMat: 0,
        otherMonthlyIncomeGross: 0,
        otherMonthlyIncomeGrossAfterMat: 0,
        otherMonthlyIncomeNet: 0.0,
        otherMonthlyIncomeNetAfterMat: 0.0,
        otherGrossAfterMat: 0.0,
        otherNetAfterMat: 0.0,
        s1GrossAfterMat: 0.0,
        s2GrossAfterMat: 0.0,
        s1NetAfterMat: 0.0,
        s2NetAfterMat: 0.0,
        totalGrossAfterMat: 0.0,
        totalNetAfterMat: 0.0,
        disposableIncomeAfterMat: 0.0,
        totalExpensesAfterMat: 0.0,
        availableMonthlyContributionFromSavings: 0.0,
        matLeaveYesNo: false,
        paymentOnLoansAndDebts: 0,
        paymentOnLoansAndDebtsAfterMat: 0,
        politicallyExposedForeignPerson: false,
        returnDateMonth: "",
        returnDateYear: "",
        returnToWorkDate: new Date(),
        riskTolerance: "",
        safety: false,
        securities: 0,
        speculation: false,
        pepYN: false,
        drReason: '',
        thirdPartyApproxNetWorth: "",
        thirdPartyBegin: "",
        thirdPartyBirthdate: new Date(),
        thirdPartyDetail: "",
        thirdPartyDisposableIncome: "",
        thirdPartyDocumentation: "",
        thirdPartyEndDate: "",
        thirdPartyFinacialSupportForm: false,
        thirdPartyHomeAddress: "",
        thirdPartyIdExpiry: "",
        thirdPartyIdNumber: "",
        thirdPartyIdType: "",
        thirdPartyIncome: "",
        thirdPartyIncomeSource: "",
        thirdPartyName: "",
        thirdPartyNoEndReason: "",
        thirdPartyNotes: "",
        thirdPartyRelationship: "",
        thirdPartyEmail: "",
        thirdPartyPhone: "",
        thirdPartyInPersonSigner: true,
        // totalFinancialAssets: 0, calculated thru calculateTotalFinancialAssets
        transportation: 0,
        transportationAfterMat: 0,
        utilities: 0,
        utilitiesAfterMat: 0,
        vehicles: 0,
        yearlyIncomeGross: 0,
        vehiclesAfterMat: 0,

        cashAndLiquid: 0,
        registeredAccountsRESPAndTFSA: 0,
        RegisteredAccountsRESPs: "",
        investmentAssetsOther: "",
        totalInvestmentAssets: "",
        principalResidenceEquity: "",
        otherRealEstateEquity: 0,
        otherAssetsVehicles: 0,
        collectablesArtValuables: 0,
        totalOtherAssets: 0,

        timeHorizon: "",

        riskProfileConclusion: "",
        whyPlanIsSuitable: "",

        groupRecommendationConsiderObjectivesAndTime: false,
        groupAbilityToMeetRequirements: false,
        groupReviewedFees: false,
        groupConsideredAchievers: false,
        groupConsideredSI: false,
        groupRecommendationConsidersRisk: false,
        groupConfirmedAbilityToMeetContributions: false,
        groupConfirmedWillingness: false,
        groupAlternativeNotSelectedDescription: "",
        groupWhyPutsInterestFirst: "",
        groupPlanMode: "",
        groupTModeContribution: 0,
        groupContributionAsPercentOfIlliquid: 0,
        groupContributionAsPercentOfInvestable: 0,
        groupOtherModesAnnualContribution: 0,
        groupContributionAsPercentOfLiquid: 0,

        achieversRecommendationConsiderObjectivesAndTime: false,
        achieversConfirmedReasonablenessOfContribution: false,
        achieversReviewedFees: false,
        achieversConsideredGroup: false,
        achieversConsideredSI: false,
        achieversRecommendationConsidersRisk: false,
        achieversConfirmedWillingness: false,
        achieversAlternativeNotSelectedDescription: "",
        achieversWhyPutsInterestFirst: "",
        achieversPlanMode: "",
        achieversTModeContribution: 0,
        achieversContributionAsPercentOfIlliquid: 0,
        achieversContributionAsPercentOfInvestable: 0,
        achieversOtherModesAnnualContribution: 0,
        achieversContributionAsPercentOfLiquid: 0,
        achieversNeedForFlexibility: false,

        SIRecommendationConsiderObjectivesAndTime: false,
        SIReviewedFees: false,
        SIRecommendationConsidersRisk: false,
        SINeedForFlexibility: false,
        SIConfirmedWillingness: false,
        SIAlternativeNotSelectedDescription: "",
        SIConsideredGroup: false,
        SIConsideredAchievers: false,
        SIWhyPutsInterestFirst: "",
        SIPlanMode: "",
        SITModeContribution: 0,
        SIContributionAsPercentOfIlliquid: 0,
        SIContributionAsPercentOfInvestable: 0,
        SIOtherModesAnnualContribution: 0,
        SIContributionAsPercentOfLiquid: 0,
    });
    const [plan, setPlan] = useCustomState({
        airMilesDollarsToRedeem: 0,
        airMilesToRedeem: 0,
        clbOnly: false,
        completionInsurance: false,
        criticalIllnessInsurance: false,
        depositAmount: 0.0,
        depositCode: 0,
        depositMode: "M",
        depositWithApplication: 0.0,
        effectiveDate: "",
        grantRoom: 0,
        initialDepositDate: "",
        initialDepositMethod: "",
        interestDeficiency: 0.0,
        maturityDate: "",
        nextDepositDate: "",
        nomineeInsurance: false,
        numberOfUnits: 1.0,
        ongoingDepositMethod: "",
        paymentProtectionInsurance: false,
        planContributionTarget: 0.0,
        planTypeID: -1,
        requestedDepositDate: "",
        siPlanLifetimeAmount: 0.0,
        totalDepositAmount: 0.0,
        totalInsuranceAmount: 0.0,
    });
    const [plan2, setPlan2] = useCustomState({
        airMilesDollarsToRedeem: 0,
        airMilesToRedeem: 0,
        clbOnly: false,
        completionInsurance: false,
        criticalIllnessInsurance: false,
        depositAmount: 0.0,
        depositCode: 0,
        depositMode: "M",
        depositWithApplication: 0.0,
        effectiveDate: "",
        grantRoom: 0,
        initialDepositDate: "",
        initialDepositMethod: "",
        interestDeficiency: 0.0,
        maturityDate: "",
        nextDepositDate: "",
        nomineeInsurance: false,
        numberOfUnits: 1.0,
        ongoingDepositMethod: "",
        paymentProtectionInsurance: false,
        planContributionTarget: 0.0,
        planTypeID: -1,
        requestedDepositDate: "",
        siPlanLifetimeAmount: 0.0,
        totalDepositAmount: 0.0,
        totalInsuranceAmount: 0.0,
    });
    const [subscriber, setSubscriber] = useCustomState({
        address: "",
        airMiles: "",
        birthdate: "",
        businessPhone: "",
        city: "",
        country: "",
        custodialParent: true,
        email: "",
        employer: "",
        employerAddress: "",
        employerIndustry: "",
        firstName: "",
        gender: 0,
        homePhone: "",
        id: "",
        idtype: "",
        investmentExperience: "",
        investmentTypes: "",
        lastName: "",
        middleName: "",
        monthlyIncomeGross: 0,
        monthlyIncomeGrossAfterMat: 0,
        monthlyIncomeNet: 0,
        monthlyIncomeNetAfterMat: 0,
        signatureDate: "",
        occupation: "",
        pcgSpouse: false,
        pobox: "",
        postalCode: "",
        primaryCaregiver: false,
        province: "",
        sin: "",
        streetNumber: "",
        suite: "",
        title: "",
        inPersonSigner: true,
        mobilePhone: "",
    });
    const [jointSubscriber, setJointSubscriber] = useCustomState({
        address: "",
        airMiles: "",
        birthdate: "",
        businessPhone: "",
        city: "",
        country: "",
        custodialParent: true,
        email: "",
        employer: "",
        employerAddress: "",
        employerIndustry: "",
        firstName: "",
        gender: 0,
        homePhone: "",
        id: "",
        idtype: "",
        investmentExperience: "",
        investmentTypes: "",
        lastName: "",
        middleName: "",
        monthlyIncomeGross: 0,
        monthlyIncomeGrossAfterMat: 0,
        monthlyIncomeNet: 0,
        monthlyIncomeNetAfterMat: 0,
        signatureDate: "",
        occupation: "",
        pcgSpouse: false,
        pobox: "",
        postalCode: "",
        primaryCaregiver: false,
        province: "",
        sin: "",
        streetNumber: "",
        suite: "",
        title: "",
        inPersonSigner: true,
        mobilePhone: "",
    });
    const [transfer, setTransfer] = useCustomState({
        enableTransfer: false, // TODO shouldn't be used as its intermediary?
        promoterAddress: "",
        promoterContractNumber: "",
        promoterName: "",
        relinquishBirthdate: new Date(),
        relinquishFirstName: "",
        relinquishGender: 0,
        relinquishIsBeneficiary: false,
        relinquishLastName: "",
        relinquishMultipleBeneficiary: false,
        relinquishSin: "",
        transferAmount: 0,
        transferAmount1: 0,
        transferAmount2: 0,
        transferAmount3: 0,
        transferBalance: true,
        transferBc: 0,
        transferClb: 0,
        transferCode1: "",
        transferCode2: "",
        transferCode3: "",
        transferPercent: false,
    });
    const [quote, setQuote] = useCustomState({
        quoteBasedOn: "units",
        depositFrequency: DEPOSIT_FREQUENCIES_FOR_QUOTE[0].value,
    });
    const [quote2, setQuote2] = useCustomState({
        quoteBasedOn: "units",
        depositFrequency: DEPOSIT_FREQUENCIES_FOR_QUOTE[0].value,
    });
    const [docusign, setDocusign] = useState({
        envelopeId: null,
        status: "pre-docusign",
    });
    const [enrollment, setEnrollment] = useState({
        _id: null,
        state: "pre-docusign",
    });
    const [drChecklist, setDrChecklist] = useCustomState({
        howDidYouMeet: "",
        howDidYouMeetDetails: "",
        enrolmentApplicationReviewed: false,
        kycDiscussion: false,
        prospectusDelivered: false,
        prospectusAmmendment: false,
        enrolmentAppBoxesExplained: false,
        cefiPlanPresentation: false,
        planSummaryExplained: false,
        relationshipDisclosureExplained: false,
        identificationInspected: false,
        grantApplicationsExplained: false,
        grantLimitsExplained: false,
        nonResidentRiskDisclosureExplained: false,
        thirdPartyFinancialSupportDisclosure: false,
        frenchEnrolmentApplicationProvided: false,
        thirdLanguageAcknowledgement: false,
        escrowAgreementExplained: false,
        changesInitialed: false,
        chequePayableToCET: false,
        dualLicenceDisclosureExplained: false,
        optionalInsuranceFormExplained: false,
        cefiTeamsBackgroundUsed: false,
        identificationScanned: false,
        identificationRecorded: false,
        identificationInspectedRemote: false,
        videoConferenceCompleted: false,
        comments: "",
    });

    return {
        user,
        setUser,
        aces,
        setAces,
        agent,
        setAgent,
        application,
        setApplication,
        beneficiary,
        setBeneficiary,
        common,
        setCommon,
        configuration,
        setConfiguration,
        dependant,
        setDependant,
        dependant2,
        setDependant2,
        dependant3,
        setDependant3,
        dependant4,
        setDependant4,
        dependant5,
        setDependant5,
        knowYourClient,
        setKnowYourClient,
        plan,
        setPlan,
        plan2,
        setPlan2,
        subscriber,
        setSubscriber,
        jointSubscriber,
        setJointSubscriber,
        transfer,
        setTransfer,
        quote,
        setQuote,
        quote2,
        setQuote2,
        docusign,
        setDocusign,
        enrollment,
        setEnrollment,
        drChecklist,
        setDrChecklist,
    };
}
